import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Card, CardContent, CircularProgress, Container, Grid, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '~/components/Header';
import Input from '~/components/Input';
import InputDate from '~/components/InputDate';
import SidebarMesages from '~/components/PersonalCoverage/SidebarMesages';
import { DocumentType } from '~/constants/documentType';
import { useQuoteData } from '~/hooks/useQuoteData';
import api from '~/services/api';
import useStore, { storeState } from '~/store';
import { reportError } from '~/utils/reportError';
import * as styles from './styles';
import { createValidationSchema } from './validationConditional';

type Inputs = {
  socialName: string;
  fullName: string;
  email: string;
  phone: string;
  documentNumber: string;
  birthDate: string;
  zipcode: string;
  state: string;
  city: string;
  neighborhood: string;
  street: string;
  numberAddress: string;
  additionalDetails: string;
};

export default function HelpZipcodePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan');
  const distribuitorParam = searchParams.get('dis');
  const orgParam = searchParams.get('org');
  const sellerParam = searchParams.get('seller');

  // Hook para recuperar dados da cotação
  const { quoteLoading } = useQuoteData({
    redirectToHome: true,
    quoteParamName: 'quote'
  });

  const {
    quoteResponse,
    quoteAsyncPostCustomer,
    quoteAsyncPostPayment,
    quote,
    customer,
    insured,
    products,
    setPhoneNumber,
    setCpf,
    setBirthDate,
    setEmail,
    setName,
    promotionData,
    contractAsyncPostPromotion
  } = useStore((store) => ({
    quoteAsyncPostCustomer: store.quoteAsyncPostCustomer,
    quoteAsyncPostPayment: store.quoteAsyncPostPayment,
    quote: store.quote,
    quotes: store.quotes,
    customer: store.customer,
    products: store.products,
    insured: store.insured,
    quoteResponse: store.quoteResponse,
    setPhoneNumber: store.SetPhoneNumber,
    setCpf: store.SetCpf,
    setBirthDate: store.SetBirthDate,
    setEmail: store.SetEmail,
    setName: store.SetName,
    promotionData: store.promotionData,
    contractAsyncPostPromotion: store.contractAsyncPostPromotion
  }));

  // Verificar se temos dados do segurado para desabilitar campos
  const hasInsuredData = insured && (insured.name || insured.document || insured.email);

  // Exibir campo Nome Social apenas quando o GET de quotes retornar social_name não nulo
  const showSocialNameField = insured?.social_name != null;

  const isPromotionFlow = promotionData?.has_promotion === true && promotionData?.is_promotion_available === true;

  const isOnlyProduct9 = products?.length === 1 && products[0]?.product_id === '9';

  const requireAddress = isPromotionFlow || isOnlyProduct9;

  const {
    handleSubmit,
    control,
    getValues,
    trigger,
    watch,
    formState: { errors, dirtyFields },
    setValue,
    clearErrors,
    reset
  } = useForm<Inputs>({
    resolver: yupResolver(createValidationSchema(insured, requireAddress)),
    defaultValues: {
      socialName: '',
      fullName: '',
      email: '',
      phone: '',
      documentNumber: '',
      birthDate: '',
      zipcode: '',
      state: '',
      city: '',
      neighborhood: '',
      street: '',
      numberAddress: '',
      additionalDetails: ''
    }
  });
  const [loading, setLoading] = useState(false);

  // useEffect para resetar o formulário com os dados corretos quando eles chegarem
  useEffect(() => {
    if (!quoteLoading && (insured || customer || quote)) {
      const formData = {
        socialName: insured?.social_name || '',
        fullName: insured?.name || customer?.name || quote?.name || '',
        email: insured?.email || customer?.email || quote?.email || '',
        phone: insured?.mobile_phone || customer?.mobile_phone || quote?.phoneNumber || '',
        documentNumber: insured?.document || customer?.document || quote?.cpf || '',
        birthDate: insured?.birthdate || customer?.birthdate || quote?.birthDate || '',
        zipcode: '',
        state: '',
        city: '',
        neighborhood: '',
        street: '',
        numberAddress: '',
        additionalDetails: ''
      };

      reset(formData);
    }
  }, [quoteLoading, insured, customer, quote, reset]);

  // Atualizar campos do formulário com dados do insured quando disponível
  useEffect(() => {
    if (hasInsuredData) {
      if (insured.social_name) setValue('socialName', insured.social_name);
      if (insured.name) setValue('fullName', insured.name);
      if (insured.email) setValue('email', insured.email);
      if (insured.mobile_phone) setValue('phone', insured.mobile_phone);
      if (insured.document) setValue('documentNumber', insured.document);
      if (insured.birthdate) {
        // Para o InputDate, vamos definir diretamente a string ISO
        setValue('birthDate', insured.birthdate);
      }
    }
  }, [insured, setValue, hasInsuredData]);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    setLoading(true);
    try {
      const payload = {
        name: typeof data.fullName === 'string' ? data.fullName.trim() : data.fullName,
        social_name: typeof data.socialName === 'string' ? data.socialName.trim() : data.socialName || '',
        email: typeof data.email === 'string' ? data.email.trim() : data.email,
        mobile_phone: data.phone,
        document: data.documentNumber,
        document_type: DocumentType.CPF,
        birthDate: data.birthDate,
        ...(orgParam && { organization_id: orgParam })
      };

      const quoteHash = quoteResponse?.quote_hash ?? searchParams.get('quote') ?? '';
      if (!quoteHash.trim()) {
        throw new Error('Identificador da cotação não encontrado.');
      }

      console.log('Payload sendo enviado:', payload);
      console.log('Data.documentNumber:', data.documentNumber);

      await quoteAsyncPostCustomer(quoteHash, payload);

      // Atualizar estado do quote
      setEmail(getValues('email'));
      setName(getValues('fullName'));
      setPhoneNumber(getValues('phone'));
      setCpf(getValues('documentNumber'));
      setBirthDate(getValues('birthDate'));

      // Atualizar também o estado insured para manter consistência
      useStore.setState((state) => ({
        ...state,
        insured: {
          ...state.insured,
          name: data.fullName,
          social_name: data.socialName,
          document: data.documentNumber,
          email: data.email,
          mobile_phone: data.phone,
          birthdate: data.birthDate
        }
      }));

      if (isOnlyProduct9) {
        const quoteId = quoteHash;
        if (!quoteId) {
          throw new Error('Identificador da cotação não encontrado.');
        }
        await quoteAsyncPostPayment({
          quote_id: quoteId,
          payment: { card_hash: '', payment_method: 'free_products' },
          tax_address: {
            number: data.numberAddress,
            additional_details: data.additionalDetails || '',
            zipcode: data.zipcode.replace(/-/g, ''),
            street: data.street,
            neighborhood: data.neighborhood,
            city: data.city,
            state: data.state
          },
          organization_id: orgParam ?? ''
        });
        setLoading(false);
        const quoteParam = searchParams.get('quote');
        const queryTail = `${quoteParam ? `&quote=${quoteParam}` : ''}${planParam ? `&plan=${planParam}` : ''}${
          distribuitorParam ? `&dis=${distribuitorParam}` : ''
        }${orgParam ? `&org=${orgParam}` : ''}${sellerParam ? `&seller=${sellerParam}` : ''}`;
        navigate(`/paymentConfirm/?${queryTail.replace(/^&/, '')}`);
        return;
      }

      if (isPromotionFlow) {
        // Fluxo de promoção: fazer POST para /v2/Contracts/promotion.
        // Usa o hash da cotação (quoteHash, resolvido da URL/store) — NÃO quote_id,
        // que fica vazio no reload e virava null via parseInt no serviço.
        await contractAsyncPostPromotion(
          quoteHash,
          {
            tax_address: {
              number: data.numberAddress,
              additional_details: data.additionalDetails || '',
              zipcode: data.zipcode.replace('-', ''),
              street: data.street,
              neighborhood: data.neighborhood,
              city: data.city,
              state: data.state
            }
          },
          orgParam ?? undefined
        );
        setLoading(false);
        const queryTail = `${distribuitorParam ? `&dis=${distribuitorParam}` : ''}${
          orgParam ? `&org=${orgParam}` : ''
        }${sellerParam ? `&seller=${sellerParam}` : ''}`;
        navigate(`/paymentConfirm/?plan=${planParam}${queryTail}`);
        return;
      }
      // Fluxo normal: navegar para pagamento (incluir plan, dis, org e channel para Payment ter contexto e chamar payment_gateway)
      setLoading(false);
      const paymentQueryTail = `${planParam ? `&plan=${planParam}` : ''}${
        distribuitorParam ? `&dis=${distribuitorParam}` : ''
      }${orgParam ? `&org=${orgParam}` : ''}${sellerParam ? `&seller=${sellerParam}` : ''}${
        searchParams.get('channel') ? `&channel=${searchParams.get('channel')}` : ''
      }`;
      navigate(`/payment/?quote=${quoteHash}${paymentQueryTail}`);
    } catch (error: any) {
      console.error('Erro ao finalizar cobertura:', error);
      reportError('Erro ao finalizar cobertura e gerar cotação', error, {
        context: 'CoverageFinish_onSubmit',
        hasQuoteData: !!quoteResponse,
        formData: data
      });
      setLoading(false);
      let errorMessage =
        'Não foi possível realizar a sua assinatura no momento. Por favor, tente novamente mais tarde.';
      if (error.isAxiosError) {
        const notifications = error.response?.data?.notifications;
        if (notifications) errorMessage = notifications[0]?.message;
      }
      storeState.addToast({
        title: 'Error',
        message: errorMessage,
        type: 'error'
      });
    }
  };

  useEffect(() => {
    watch((_data, { name, type }) => {
      if (name === 'documentNumber' && type === 'change') {
        trigger('documentNumber');
      }
    });
  }, [watch]);

  const zipcode = watch('zipcode');

  useEffect(() => {
    const cleanZipcode = zipcode?.replace('-', '').trim();

    if (cleanZipcode && cleanZipcode.length === 8) {
      api.v2!.get(`ZipCodes?zipcode=${cleanZipcode}`).then(({ data: { data } }) => {
        console.log('Dados do CEP recebidos:', data);

        Object.entries(data).forEach(([key, value]) => {
          setValue(key as keyof Inputs, value as string, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true
          });
        });

        clearErrors('zipcode');
        setTimeout(() => {
          trigger(['state', 'city', 'neighborhood', 'street']);
        }, 100);
      });
    }
  }, [zipcode, setValue, clearErrors, trigger]);

  // Mostrar loading enquanto carrega os dados da cotação
  if (quoteLoading) {
    return (
      <Container maxWidth="lg" sx={styles.container}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress size={40} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={styles.container}>
      <Grid container spacing={2}>
        <Grid item md={8} xs={12}>
          <Card>
            <CardContent sx={{ padding: { sm: 3 } }}>
              <Header
                title="Vamos finalizar a sua proposta?"
                goBackTo={`/personalCoverage/?quote=${searchParams.get('quote')}&plan=${planParam}${
                  distribuitorParam ? `&dis=${distribuitorParam}` : ''
                }${orgParam ? `&org=${orgParam}` : ''}${sellerParam ? `&seller=${sellerParam}` : ''}`}
              />
              <Box sx={styles.descriptionContainer}>
                <Typography variant="body1">
                  Não se preocupe, os dados sobre o ciclista e o equipamento serão solicitados logo após esta etapa de
                  pagamento!
                </Typography>
              </Box>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={styles.containerForm}>
                  {showSocialNameField && (
                    <Controller
                      name="socialName"
                      control={control}
                      render={({ field }) => (
                        <Input
                          label="Nome Social"
                          placeholder="Seu nome social"
                          error={errors?.socialName ? true : false}
                          isDirty={dirtyFields.socialName}
                          helperText={errors?.socialName?.message}
                          {...field}
                        />
                      )}
                    />
                  )}
                  <Controller
                    name="fullName"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Input
                          label="Seu nome civil"
                          placeholder="Seu nome civil"
                          error={errors?.fullName ? true : false}
                          isDirty={dirtyFields.fullName}
                          helperText={errors?.fullName?.message}
                          {...field}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          O nome civil informado será utilizado apenas para fins contratuais e fiscais.
                          {showSocialNameField &&
                            ' Para melhor atender às suas necessidades, nos comunique o seu nome social!'}
                        </Typography>
                      </>
                    )}
                  />
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Input
                          label="Seu email"
                          type="email"
                          placeholder="seuemail@email.com"
                          error={errors?.email ? true : false}
                          isDirty={dirtyFields.email}
                          helperText={errors?.email?.message}
                          {...field}
                        />
                      </>
                    )}
                  />
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Input
                          label="Celular"
                          placeholder="(XX) X XXXX-XXXX"
                          mask={'phone'}
                          isDirty={dirtyFields.phone}
                          error={errors?.phone?.message ? true : false}
                          helperText={errors?.phone?.message}
                          maxRows={1}
                          {...field}
                        />
                      </>
                    )}
                  />
                  <Controller
                    name="documentNumber"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Input
                          label="CPF"
                          mask={'cpf'}
                          isDirty={dirtyFields.documentNumber}
                          error={errors?.documentNumber?.message ? true : false}
                          helperText={errors?.documentNumber?.message}
                          placeholder="XXX.XXX.XXX-XX"
                          disabled={!!(hasInsuredData && insured?.document)}
                          {...field}
                        />
                      </>
                    )}
                  />
                  <Controller
                    name="birthDate"
                    control={control}
                    render={({ field }) => {
                      // Criar defaultValue usando os dados disponíveis
                      let defaultDateValue: Date | undefined = undefined;

                      if (field.value) {
                        defaultDateValue = new Date(field.value);
                      } else if (insured?.birthdate) {
                        defaultDateValue = new Date(insured.birthdate);
                      } else if (quote?.birthDate) {
                        defaultDateValue = new Date(quote.birthDate);
                      }

                      return (
                        <>
                          <InputDate
                            label="Data de nascimento"
                            placeholder="XX/XX/XXXX"
                            defaultValue={defaultDateValue}
                            isDirty={dirtyFields.birthDate}
                            error={errors?.birthDate?.message ? true : false}
                            helperText={errors?.birthDate?.message}
                            disabled={!!(hasInsuredData && insured?.birthdate)}
                            setField={(value) => {
                              setValue('birthDate', value);
                            }}
                            {...field}
                          />
                        </>
                      );
                    }}
                  />

                  {requireAddress && (
                    <>
                      {/* Seção Endereço */}
                      <Box sx={{ marginTop: 3 }}>
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', color: 'primary.secodary' }}>
                          Endereço
                        </Typography>
                      </Box>

                      {/* CEP com Número */}
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Controller
                            name="zipcode"
                            control={control}
                            render={({ field }) => (
                              <>
                                <Input
                                  label="CEP"
                                  placeholder="XXXXX-XXX"
                                  mask="zipcode"
                                  isDirty={dirtyFields.zipcode}
                                  error={errors?.zipcode?.message ? true : false}
                                  helperText={errors?.zipcode?.message}
                                  {...field}
                                />
                              </>
                            )}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Controller
                            name="numberAddress"
                            control={control}
                            render={({ field }) => (
                              <>
                                <Input
                                  label="Número"
                                  placeholder="Número da residência"
                                  isDirty={dirtyFields.numberAddress}
                                  error={errors?.numberAddress?.message ? true : false}
                                  helperText={errors?.numberAddress?.message}
                                  {...field}
                                />
                              </>
                            )}
                          />
                        </Grid>
                      </Grid>

                      {/* Complemento com Estado/UF */}
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Controller
                            name="additionalDetails"
                            control={control}
                            render={({ field }) => (
                              <>
                                <Input
                                  label="Complemento"
                                  placeholder="Apto ou bloco"
                                  isDirty={dirtyFields.additionalDetails}
                                  error={errors?.additionalDetails?.message ? true : false}
                                  helperText={errors?.additionalDetails?.message}
                                  {...field}
                                />
                              </>
                            )}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Controller
                            name="state"
                            control={control}
                            render={({ field }) => (
                              <>
                                <Input
                                  label="Estado/UF"
                                  placeholder="Digite primeiro o CEP"
                                  inputProps={{ maxLength: 2 }}
                                  isDirty={dirtyFields.state}
                                  error={errors?.state?.message ? true : false}
                                  helperText={errors?.state?.message}
                                  {...field}
                                />
                              </>
                            )}
                          />
                        </Grid>
                      </Grid>

                      {/* Cidade (linha completa) */}
                      <Controller
                        name="city"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Input
                              label="Cidade"
                              placeholder="Digite primeiro o CEP"
                              isDirty={dirtyFields.city}
                              error={errors?.city?.message ? true : false}
                              helperText={errors?.city?.message}
                              {...field}
                            />
                          </>
                        )}
                      />

                      {/* Bairro (linha completa) */}
                      <Controller
                        name="neighborhood"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Input
                              label="Bairro"
                              placeholder="Digite primeiro o CEP"
                              isDirty={dirtyFields.neighborhood}
                              error={errors?.neighborhood?.message ? true : false}
                              helperText={errors?.neighborhood?.message}
                              {...field}
                            />
                          </>
                        )}
                      />

                      {/* Endereço (linha completa) */}
                      <Controller
                        name="street"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Input
                              label="Endereço"
                              placeholder="Digite primeiro o CEP"
                              isDirty={dirtyFields.street}
                              error={errors?.street?.message ? true : false}
                              helperText={errors?.street?.message}
                              {...field}
                            />
                          </>
                        )}
                      />
                    </>
                  )}
                </Box>
                <Box sx={styles.containerButton}>
                  <LoadingButton variant="contained" type="submit" loading={loading}>
                    Vamos lá
                  </LoadingButton>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          item
          md={4}
          xs={12}
          sx={{
            display: {
              xs: 'none',
              lg: 'block'
            }
          }}
        >
          <Card>
            <CardContent>
              <SidebarMesages />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
